import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { ApiError } from '../../api/client';
import { useDetailInView } from '../../components/ui/useDetailInView';
import { num } from '../../format';
import { useAuth } from '../auth/AuthContext';
import type { Focus } from '../layout/focus';
import { useSelectedId } from '../layout/useSelectedId';
import { PurchasePanel } from './PurchasePanel';
import { VendorCard, VendorForm } from './VendorForm';
import { createPurchase, listPurchases, listVendors, type PurchaseStatus } from './api';

const FILTERS: (PurchaseStatus | '')[] = [
  '',
  'draft',
  'ordered',
  'shipped',
  'partial',
  'received',
  'cancelled',
];

/**
 * Orders on the left, the open order on the right.
 *
 * The list leads with status and vendor because the question people bring to
 * this screen is "where is my parcel" — the code matters only once the parcel is
 * on the bench and needs putting away.
 */
export function PurchasesPage({ focus }: { focus?: Focus | null }) {
  const { t } = useTranslation();
  const { can } = useAuth();
  const queryClient = useQueryClient();

  const [filter, setFilter] = useState<PurchaseStatus | ''>('');
  const [query, setQuery] = useState('');
  const [selectedId, setSelectedId] = useSelectedId('/purchases');
  const [creating, setCreating] = useState(false);
  const [dirty, setDirty] = useState(false);

  const canWrite = can('purchase.write');

  const orders = useQuery({
    queryKey: ['purchases', 'list', filter, query],
    queryFn: () => listPurchases(filter || undefined, query || undefined),
  });
  const vendors = useQuery({ queryKey: ['reference', 'vendors'], queryFn: listVendors });

  // Arrived here from a movement in a component's history: that order is what
  // the person clicked, so it is what opens.
  useEffect(() => {
    if (!focus) return;
    setCreating(false);
  }, [focus]);

  const detail = useDetailInView(`${selectedId}/${creating}`);

  const vendorName = (id: string | null) =>
    vendors.data?.find((row) => row.id === id)?.name ?? '';

  const showingDetail = Boolean(selectedId || creating);
  const open = (id: string) => {
    if (id !== selectedId && dirty && !window.confirm(t('purchases.discardChanges'))) return;
    setDirty(false);
    setSelectedId(id);
    setCreating(false);
  };

  return (
    <div className={`purchases${showingDetail ? ' is-detail' : ''}`}>
      <aside className="purchases__list">
        {/* Searches the lines, not only the order. Nobody remembers an order by
            its code — they remember what was on it. */}
        <div className="components__search">
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={t('purchases.searchPlaceholder')}
            aria-label={t('purchases.search')}
          />
        </div>

        <div className="components__search">
          <select
            value={filter}
            onChange={(event) => setFilter(event.target.value as PurchaseStatus | '')}
          >
            {FILTERS.map((value) => (
              <option key={value || 'any'} value={value}>
                {value ? t(`purchases.status.${value}`) : t('purchases.anyStatus')}
              </option>
            ))}
          </select>
          {canWrite && (
            <button
              type="button"
              onClick={() => {
                setCreating(true);
                setSelectedId(null);
              }}
            >
              {t('purchases.add')}
            </button>
          )}
        </div>

        {orders.isLoading && <p className="panel__hint">{t('common.loading')}</p>}
        {orders.data?.length === 0 && <p className="panel__hint">{t('purchases.empty')}</p>}

        <ul className="components__results">
          {(orders.data ?? []).map((order) => {
            const ordered = order.lines.reduce((sum, line) => sum + Number(line.quantity), 0);
            const received = order.lines.reduce(
              (sum, line) => sum + Number(line.received_quantity),
              0,
            );
            // What is on the order, on hover. The question "was that the order
            // with the connectors on it" is asked of every row in the list, and
            // answering it by opening each one is the work this removes.
            const contents = order.lines
              .map(
                (line) =>
                  `${num(line.quantity)} × ${line.component?.name ?? line.component_id}` +
                  (line.component?.mpn ? ` (${line.component.mpn})` : ''),
              )
              .join('\n');
            return (
              <li key={order.id}>
                <button
                  type="button"
                  className={order.id === selectedId ? 'is-selected' : undefined}
                  title={contents || undefined}
                  onClick={() => open(order.id)}
                >
                  <span className="components__name">
                    {vendorName(order.vendor_id) || t('purchases.noVendor')}
                    <span className={`purchase__status purchase__status--${order.status}`}>
                      {t(`purchases.status.${order.status}`)}
                    </span>
                  </span>
                  <span className="components__meta">
                    <span className="components__code">{order.code}</span>
                    {order.lines.length > 0 && (
                      <em>
                        {num(received)} / {num(ordered)}
                      </em>
                    )}
                    {order.total_cost && (
                      <em>
                        {num(order.total_cost)} {order.currency}
                      </em>
                    )}
                    {order.note && (
                      <em className="purchase__note" title={order.note}>
                        {order.note}
                      </em>
                    )}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      </aside>

      <div className="purchases__detail" ref={detail}>
        <button
          type="button"
          className="pane-back"
          onClick={() => {
            setSelectedId(null);
            setCreating(false);
          }}
        >
          ← {t('common.backToList')}
        </button>

        {creating ? (
          <NewPurchaseForm
            onCreated={(id) => {
              setCreating(false);
              setSelectedId(id);
              void queryClient.invalidateQueries({ queryKey: ['purchases'] });
            }}
            onCancel={() => setCreating(false)}
          />
        ) : selectedId ? (
          <PurchasePanel
            key={selectedId}
            purchaseId={selectedId}
            onArchived={() => setSelectedId(null)}
            onDirtyChange={setDirty}
          />
        ) : (
          <p className="panel__hint">{t('purchases.selectHint')}</p>
        )}
      </div>
    </div>
  );
}

/** An order starts with where it was bought and in what currency, because those
 *  two are what its prices mean. Everything else is filled in as the parcel
 *  travels. */
function NewPurchaseForm({
  onCreated,
  onCancel,
}: {
  onCreated: (id: string) => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();

  const [vendorId, setVendorId] = useState('');
  const [addingVendor, setAddingVendor] = useState(false);
  const [currency, setCurrency] = useState('RUB');
  // Today, because an order is written down on the day it is placed. Wrong far
  // less often than blank, and a blank date is one nobody goes back to fill in.
  const [orderedAt, setOrderedAt] = useState(() => new Date().toISOString().slice(0, 10));
  const [expectedAt, setExpectedAt] = useState('');
  const [orderUrl, setOrderUrl] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  const vendors = useQuery({ queryKey: ['reference', 'vendors'], queryFn: listVendors });
  const vendor = vendors.data?.find((row) => row.id === vendorId) ?? null;

  const create = useMutation({
    mutationFn: () =>
      createPurchase({
        vendor_id: vendorId || null,
        currency,
        ordered_at: orderedAt || null,
        expected_at: expectedAt || null,
        order_url: orderUrl || null,
        note: note || null,
      }),
    onSuccess: (order) => onCreated(order.id),
    onError: (exc) => setError(exc instanceof ApiError ? exc.code : 'unknown'),
  });

  if (addingVendor) {
    return (
      <VendorForm
        onSaved={(saved) => {
          setVendorId(saved.id);
          setAddingVendor(false);
        }}
        onCancel={() => setAddingVendor(false)}
      />
    );
  }

  return (
    <form
      className="component-form"
      onSubmit={(event) => {
        event.preventDefault();
        create.mutate();
      }}
    >
      <h2>{t('purchases.new')}</h2>

      <div className="component-form__row">
        <label>
          {t('purchases.vendor')}
          <select value={vendorId} onChange={(event) => setVendorId(event.target.value)}>
            <option value="">{t('purchases.noVendor')}</option>
            {(vendors.data ?? []).map((row) => (
              <option key={row.id} value={row.id}>
                {row.name}
              </option>
            ))}
          </select>
        </label>

        <label>
          {t('purchases.addVendor')}
          <button type="button" onClick={() => setAddingVendor(true)}>
            {t('purchases.addVendorShort')}
          </button>
        </label>
      </div>

      {vendor && <VendorCard vendor={vendor} onEdit={() => setAddingVendor(true)} />}

      <div className="component-form__row">
        <label>
          {t('purchases.currency')}
          <input
            value={currency}
            onChange={(event) => setCurrency(event.target.value.toUpperCase())}
            maxLength={3}
            size={4}
          />
        </label>

        <label>
          {t('purchases.orderedAt')}
          <input
            type="date"
            value={orderedAt}
            onChange={(event) => setOrderedAt(event.target.value)}
          />
        </label>
        <label>
          {t('purchases.expectedAt')}
          <input type="date" value={expectedAt} onChange={(event) => setExpectedAt(event.target.value)} />
        </label>
      </div>

      {/* The order on the shop's site: months later it is the only place that
          still knows what exactly was bought and at what price. */}
      <label>
        {t('purchases.orderUrl')}
        <input
          type="url"
          value={orderUrl}
          onChange={(event) => setOrderUrl(event.target.value)}
          placeholder="https://"
        />
      </label>

      <label>
        {t('purchases.note')}
        <input value={note} onChange={(event) => setNote(event.target.value)} />
      </label>

      {error && (
        <p className="panel__error">
          {t(`purchases.errors.${error}`, t('purchases.errors.unknown'))}
        </p>
      )}

      <div className="component-form__actions">
        <button type="submit" disabled={create.isPending}>
          {t('common.save')}
        </button>
        <button type="button" onClick={onCancel}>
          {t('common.cancel')}
        </button>
      </div>
    </form>
  );
}
